import React, { useRef } from 'react';
import { Form } from 'react-bootstrap';

interface IFileInputProps {
    label: string;
    value?: File;
    onChange: (file?: File) => void;
    /** Accepted extensions, e.g. '.pdf,.jpg'. Keep in sync with what the endpoint allows. */
    accept?: string;
    maxSizeBytes?: number;
    controlId: string;
    className?: string;
    required?: boolean;
    disabled?: boolean;
    /** When true, a missing or oversized file is shown as an error. */
    validated?: boolean;
    placeholder?: string;
}

export const DEFAULT_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

export function isFileTooLarge(file?: File, maxSizeBytes: number = DEFAULT_MAX_FILE_SIZE_BYTES): boolean {
    return !!file && file.size > maxSizeBytes;
}

export function isFileTypeUnaccepted(file?: File, accept?: string): boolean {
    if (!file || !accept) return false;

    const extensions = accept.split(',').map(e => e.trim().toLowerCase()).filter(Boolean);
    if (extensions.length === 0) return false;

    const name = file.name.toLowerCase();

    return !extensions.some(extension => name.endsWith(extension));
}

function toMegabytes(bytes: number): number {
    return Math.round(bytes / (1024 * 1024));
}

/** The accepted extensions as readable text, e.g. '.pdf,.docx' becomes 'PDF või DOCX fail'. */
function toAcceptLabel(accept?: string): string {
    const names = (accept ?? '')
        .split(',')
        .map(extension => extension.trim().replace(/^\./, '').toUpperCase())
        .filter(Boolean);

    if (names.length === 0) return 'lubatud tüüpi fail';
    if (names.length === 1) return `${names[0]} fail`;

    return `${names.slice(0, -1).join(', ')} või ${names[names.length - 1]} fail`;
}

export function FileInput(props: Readonly<IFileInputProps>) {
    const {
        label,
        value,
        onChange,
        accept,
        maxSizeBytes = DEFAULT_MAX_FILE_SIZE_BYTES,
        controlId,
        className,
        required,
        disabled,
        validated,
        placeholder = 'Faili ei ole valitud'
    } = props;

    const inputRef = useRef<HTMLInputElement>(null);

    const isTooLarge = isFileTooLarge(value, maxSizeBytes);
    const isWrongType = isFileTypeUnaccepted(value, accept);
    const isMissing = !!required && !value;

    const isInvalid = isWrongType || (!!validated && (isMissing || isTooLarge));
    const isClearable = !!value && !disabled;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        onChange(files && files.length > 0 ? files[0] : undefined);
    };

    const getErrorMessage = () => {
        if (isWrongType) return `Fail peab olema ${toAcceptLabel(accept)}`;
        if (isTooLarge) return `Faili maksimaalne suurus on ${toMegabytes(maxSizeBytes)}MB`;

        return 'Kohustuslik väli';
    };

    const handleClear = () => {
        // The native input keeps its value after a re-render, so it has to be reset for
        // re-picking the same file to fire another change event.
        if (inputRef.current) inputRef.current.value = '';
        onChange(undefined);
    };

    return (
        <Form.Group controlId={controlId} className={className}>
            <Form.Label>{label}</Form.Label>
            <div className={`custom-file file-input ${isClearable ? 'file-input--clearable' : ''}`}>
                <input
                    ref={inputRef}
                    id={controlId}
                    type="file"
                    accept={accept}
                    disabled={disabled}
                    onChange={handleChange}
                    className={`custom-file-input ${isInvalid ? 'is-invalid' : ''}`}
                />
                <label
                    className="custom-file-label text-truncate"
                    htmlFor={controlId}
                    style={{ paddingRight: '4rem' }}
                    data-browse="Vali"
                >
                    {value ? value.name : placeholder}
                </label>
                {isClearable && (
                    <button
                        type="button"
                        className="file-input__clear"
                        onClick={handleClear}
                        title="Eemalda fail"
                        aria-label="Eemalda fail"
                    >
                        <i className="d-inline-block icon--12 icon__close" />
                    </button>
                )}
                {isInvalid && (
                    <div className="d-block text-left invalid-feedback">
                        {getErrorMessage()}
                    </div>
                )}
            </div>
        </Form.Group>
    );
}
